import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowUpRight, MessageCircle, Cctv, ScanSearch, ClipboardCheck, ShieldAlert, Siren,
  Zap, HardHat, RefreshCw, Bell, EyeOff, Brain, Target, Cpu, BellRing,
  ShieldCheck, UserCheck, Timer, TrendingUp, Factory, Warehouse, HeartPulse, Cog, CircleDot,
  EyeOff as ChallengeEye, AlertTriangle,
} from "lucide-react";
import { Reveal } from "../../components/Reveal";
import { WA_NUMBER } from "../../data/site";

const HIGHLIGHTS = ["Real-Time PPE Monitoring", "AI-Powered Detection", "Automated Safety Alerts", "Continuous Workplace Monitoring"];

const CHALLENGES = [
  { icon: ChallengeEye, title: "Manual Monitoring Limitations", desc: "Continuous manual inspection across large work areas can be difficult and resource-intensive." },
  { icon: ShieldAlert, title: "Safety Non-Compliance", desc: "Workers may enter or operate in designated areas without the required safety equipment." },
  { icon: AlertTriangle, title: "Increased Workplace Risk", desc: "Failure to identify missing PPE in time can increase the risk of workplace hazards and accidents." },
];

const WORKFLOW = [
  { icon: Cctv, step: "Monitor", desc: "Camera feeds continuously monitor workers in designated or restricted zones." },
  { icon: ScanSearch, step: "Detect", desc: "AI-powered computer vision detects people and identifies relevant PPE." },
  { icon: ClipboardCheck, step: "Verify", desc: "The system analyzes whether the required PPE is being worn based on the configured safety requirements." },
  { icon: ShieldAlert, step: "Identify Non-Compliance", desc: "If required PPE is missing, the system identifies the safety violation in real time." },
  { icon: Siren, step: "Alert", desc: "The system triggers a configured alert or notification, such as an alarm, email, or SMS notification." },
];

const CAPABILITIES = [
  { icon: Zap, title: "Real-Time PPE Detection", desc: "Continuously monitors workers and detects PPE compliance in real time." },
  { icon: HardHat, title: "Multi-PPE Monitoring", desc: "Can detect configured safety equipment such as helmets, gloves, face shields, or full PPE kits." },
  { icon: RefreshCw, title: "Automated Compliance Monitoring", desc: "Helps automate the monitoring of workplace PPE requirements." },
  { icon: Bell, title: "Instant Alerts & Notifications", desc: "Can send warnings through configured alarms, email, or SMS notifications." },
  { icon: Cctv, title: "Continuous Camera-Based Monitoring", desc: "Uses camera feeds to monitor designated workspaces and restricted zones." },
  { icon: EyeOff, title: "Reduced Manual Inspection", desc: "Reduces dependence on continuous manual PPE inspection." },
];

const TECH = [
  { icon: Brain, title: "Computer Vision & Deep Learning", desc: "Used to detect PPE compliance from camera feeds in real time." },
  { icon: Target, title: "YOLO Object Detection", desc: "Used for fast and accurate detection of people and relevant safety equipment." },
  { icon: Cpu, title: "Edge AI & IoT Integration", desc: "Supports real-time monitoring and integration with existing security or operational systems." },
  { icon: BellRing, title: "Alert & Notification Systems", desc: "Supports configured warnings and notifications through mechanisms such as alarms, email, or SMS." },
];

const IMPACT = [
  { icon: ShieldCheck, title: "Improved Safety Compliance", desc: "Supports continuous monitoring of workplace PPE requirements." },
  { icon: UserCheck, title: "Reduced Manual Monitoring", desc: "Helps reduce the effort required for continuous manual PPE inspections." },
  { icon: Timer, title: "Faster Detection of Non-Compliance", desc: "Identifies missing PPE in real time, enabling quicker response." },
  { icon: TrendingUp, title: "Improved Operational Efficiency", desc: "Allows safety and security personnel to focus on other critical responsibilities." },
];

const DEPLOYMENTS = [
  { icon: Factory, label: "Manufacturing Facilities" },
  { icon: HardHat, label: "Construction Sites" },
  { icon: Warehouse, label: "Warehouses" },
  { icon: HeartPulse, label: "Healthcare Facilities" },
  { icon: Cog, label: "Industrial Plants" },
  { icon: CircleDot, label: "Restricted Work Zones" },
];

const BlockTitle = ({ kicker, children }) => (
  <Reveal>
    <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-smipl-orange mb-3">{kicker}</p>
    <h2 className="font-display text-2xl md:text-4xl font-bold tracking-tight mb-10">{children}</h2>
  </Reveal>
);

const LiveBadge = ({ label }) => (
  <div className="absolute top-4 left-4 rounded-full bg-navy-deep/80 backdrop-blur px-3.5 py-1.5 font-mono text-[10px] tracking-[0.25em] uppercase text-smipl-orange border border-smipl-orange/30 flex items-center gap-2">
    <span className="relative flex h-1.5 w-1.5">
      <span className="animate-pulse-ring absolute inline-flex h-full w-full rounded-full bg-smipl-orange" />
      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-smipl-orange" />
    </span>
    {label}
  </div>
);

const PpeDetectionPage = ({ openContact }) => (
  <main className="pt-36 pb-28 min-h-screen" data-testid="product-page-ppe-detection">
    <div className="max-w-6xl mx-auto px-6">
      <Reveal>
        <Link to="/products" data-testid="ppe-back-link" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] uppercase text-slate-500 hover:text-smipl-orange transition-colors">
          <ArrowLeft size={13} /> Our Products <span className="text-white/20">/</span>
          <span className="text-slate-400 normal-case tracking-normal">PPE Detection System</span>
        </Link>
      </Reveal>

      <div className="mt-10 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <Reveal delay={0.05}>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-smipl-orange">Video Analytics & Surveillance AI</p>
          </Reveal>
          <Reveal delay={0.12}>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08]">
              PPE Detection System
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed">
              An AI-powered workplace safety solution that continuously monitors workers using camera-based computer
              vision and detects whether required Personal Protective Equipment (PPE) is being worn in designated or
              restricted areas. When required PPE is missing, the system can trigger alerts and notify supervisors,
              helping organizations improve workplace safety monitoring and compliance.
            </p>
          </Reveal>
          <Reveal delay={0.28}>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {HIGHLIGHTS.map((h) => (
                <span key={h} className="rounded-full border border-white/12 bg-white/[0.04] px-4 py-2 text-xs font-medium text-slate-200">
                  {h}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.35}>
            <div className="mt-9 flex flex-wrap gap-4">
              <button data-testid="ppe-talk-btn" onClick={openContact} className="flex items-center gap-2 rounded-full bg-smipl-orange text-white px-7 py-3.5 text-sm font-semibold hover:bg-smipl-amber hover:glow-orange transition-all duration-300">
                Discuss Your Requirement <ArrowUpRight size={15} />
              </button>
              <Link to="/products" data-testid="ppe-back-btn" className="flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-white hover:border-smipl-orange hover:text-smipl-orange transition-all duration-300">
                <ArrowLeft size={15} /> Back to Products
              </Link>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.25}>
          <div className="relative rounded-3xl overflow-hidden border border-white/12 shadow-2xl shadow-black/50">
            <img src="/products/ppe-detection-1.png" alt="AI PPE detection monitoring workers on a construction site" className="w-full object-cover" />
            <motion.div
              initial={{ top: "-10%" }}
              animate={{ top: "110%" }}
              transition={{ duration: 3.2, repeat: Infinity, ease: "linear" }}
              className="absolute left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-smipl-orange to-transparent opacity-80"
            />
            <LiveBadge label="Live Monitoring" />
          </div>
        </Reveal>
      </div>

      <section className="mt-28" data-testid="ppe-challenge">
        <BlockTitle kicker="The Problem">The Challenge</BlockTitle>
        <Reveal delay={0.05}>
          <p className="max-w-3xl text-slate-400 text-base leading-relaxed mb-10">
            In industries such as manufacturing, construction, and healthcare, ensuring that workers consistently wear
            the required Personal Protective Equipment is critical for workplace safety. Manual PPE monitoring can be
            inefficient and dependent on continuous human supervision — missed violations may increase workplace safety
            risks and contribute to non-compliance with safety requirements.
          </p>
        </Reveal>
        <div className="grid sm:grid-cols-3 gap-5">
          {CHALLENGES.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div className="tracing-border h-full rounded-2xl border border-white/8 bg-navy-deep/60 p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-500/10 border border-red-500/25 text-red-400">
                  <c.icon size={19} />
                </div>
                <h3 className="mt-5 font-display text-lg font-bold">{c.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mt-28" data-testid="ppe-solution">
        <div className="rounded-3xl border border-smipl-orange/25 bg-gradient-to-br from-smipl-orange/[0.08] to-transparent p-10 md:p-14 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <BlockTitle kicker="The Solution">AI-Powered PPE Compliance Monitoring</BlockTitle>
            <Reveal delay={0.08}>
              <div className="space-y-4 text-slate-300 text-base leading-relaxed -mt-4">
                <p>The PPE Detection System uses AI-powered computer vision to continuously monitor workers through camera feeds.</p>
                <p>
                  The system detects whether required safety equipment is being worn, such as helmets, gloves, face shields,
                  or other configured PPE. When non-compliance is detected, the system can trigger alerts and notify
                  supervisors or integrate with configured warning and response mechanisms.
                </p>
                <p>This enables continuous automated monitoring while reducing dependence on manual PPE inspections.</p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="relative rounded-2xl overflow-hidden border border-white/12">
              <img src="/products/ppe-detection-2.png" alt="Real-time PPE compliance detection with safety status overlays" className="w-full object-cover" />
              <div className="absolute bottom-4 right-4 rounded-full bg-navy-deep/80 backdrop-blur px-3.5 py-1.5 font-mono text-[10px] tracking-[0.25em] uppercase text-smipl-orange border border-smipl-orange/30">
                Compliance Detection
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mt-28" data-testid="ppe-workflow">
        <BlockTitle kicker="Workflow">How the System Works</BlockTitle>
        <div className="relative">
          <div className="hidden lg:block absolute top-[52px] left-0 right-0 h-px bg-white/10" />
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            className="hidden lg:block absolute top-[52px] left-0 right-0 h-px bg-smipl-orange origin-left"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {WORKFLOW.map((w, i) => (
              <Reveal key={w.step} delay={i * 0.1}>
                <div data-testid={`ppe-step-${i + 1}`} className="relative rounded-2xl border border-white/8 bg-navy-deep/60 p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                  <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-smipl-orange text-navy-deep font-mono text-xs font-bold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <w.icon size={20} className="mt-5 text-smipl-orange" />
                  <h3 className="mt-3 font-display text-base font-bold">{w.step}</h3>
                  <p className="mt-2 text-xs text-slate-400 leading-relaxed">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-28" data-testid="ppe-showcase">
        <Reveal>
          <div className="relative rounded-3xl overflow-hidden border border-white/12">
            <img src="/products/ppe-detection-3.png" alt="Continuous PPE compliance tracking across sequential camera frames" className="w-full object-cover" />
            <LiveBadge label="Continuous Compliance Tracking" />
          </div>
        </Reveal>
      </section>

      <section className="mt-28" data-testid="ppe-capabilities">
        <BlockTitle kicker="Capabilities">Key Capabilities</BlockTitle>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CAPABILITIES.map((c, i) => (
            <Reveal key={c.title} delay={(i % 3) * 0.07}>
              <div className="tracing-border group h-full rounded-2xl border border-white/8 bg-navy-deep/60 p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-smipl-orange/10 border border-smipl-orange/25 text-smipl-orange group-hover:bg-smipl-orange group-hover:text-white transition-all duration-300">
                  <c.icon size={19} />
                </div>
                <h3 className="mt-5 font-display text-base font-bold">{c.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mt-28" data-testid="ppe-technology">
        <BlockTitle kicker="Technology">Technology Behind the Solution</BlockTitle>
        <div className="grid sm:grid-cols-2 gap-5">
          {TECH.map((t, i) => (
            <Reveal key={t.title} delay={(i % 2) * 0.08}>
              <div className="h-full rounded-2xl border border-white/8 bg-gradient-to-br from-white/[0.05] to-transparent p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                <t.icon size={26} className="text-smipl-orange" />
                <h3 className="mt-5 font-display text-lg font-bold font-mono tracking-wide">{t.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{t.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mt-28" data-testid="ppe-impact">
        <BlockTitle kicker="Business Impact">Building a Safer and Smarter Workplace</BlockTitle>
        <div className="grid sm:grid-cols-2 gap-5">
          {IMPACT.map((c, i) => (
            <Reveal key={c.title} delay={(i % 2) * 0.08}>
              <div className="group h-full rounded-2xl border border-white/8 bg-gradient-to-br from-white/[0.05] to-transparent p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40">
                <c.icon size={26} className="text-smipl-orange" />
                <h3 className="mt-5 font-display text-xl font-bold">{c.title}</h3>
                <p className="mt-2 text-sm md:text-base text-slate-400 leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mt-28" data-testid="ppe-deployments">
        <BlockTitle kicker="Deployments">Where It Can Be Deployed</BlockTitle>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {DEPLOYMENTS.map((d, i) => (
            <Reveal key={d.label} delay={(i % 3) * 0.06}>
              <div className="flex items-center gap-3.5 rounded-2xl border border-white/8 bg-white/[0.03] px-5 py-5 transition-all duration-300 hover:border-smipl-orange/40 hover:-translate-y-0.5">
                <d.icon size={18} className="shrink-0 text-smipl-orange" />
                <span className="text-sm font-medium text-slate-200">{d.label}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Reveal delay={0.1}>
        <div className="mt-28 rounded-3xl border border-smipl-orange/25 bg-gradient-to-br from-smipl-orange/10 to-transparent p-10 md:p-14 text-center" data-testid="ppe-cta">
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            Make Workplace Safety Monitoring <span className="text-smipl-orange">Smarter</span>
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-slate-400 text-base leading-relaxed">
            Discover how AI-powered PPE detection can help your organization continuously monitor safety compliance
            and identify missing protective equipment in real time.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <button data-testid="ppe-experts-btn" onClick={openContact} className="flex items-center gap-2 rounded-full bg-smipl-orange text-white px-8 py-4 text-sm font-semibold hover:bg-smipl-amber hover:glow-orange transition-all duration-300">
              Talk to Our Experts <ArrowUpRight size={15} />
            </button>
            <button
              data-testid="ppe-whatsapp-btn"
              onClick={() => (WA_NUMBER ? window.open(`https://wa.me/${WA_NUMBER}`, "_blank") : openContact())}
              className="flex items-center gap-2 rounded-full border border-wa/50 text-wa px-8 py-4 text-sm font-semibold hover:bg-wa hover:text-navy-deep transition-all duration-300"
            >
              <MessageCircle size={15} /> WhatsApp
            </button>
          </div>
        </div>
      </Reveal>
    </div>
  </main>
);

export default PpeDetectionPage;
